import type { SceneCanvasState } from "../scene/model";
import { HistoryMediaPool, type HistoryResourceRef } from "./media-pool";

type StoredState = Omit<SceneCanvasState, "assets"> & {
  assets: {
    id: string;
    kind: "image" | "audio";
    source: string | HistoryResourceRef;
  }[];
};

/** Renderer-independent Undo/Redo snapshots. Scheduling and UI stay in the host. */
export class SceneHistory {
  private snapshots: string[] = [];
  private cursor = -1;
  private readonly media: HistoryMediaPool;
  private batchDepth = 0;
  private batchPending = false;

  constructor(
    private readonly limit = 50,
    mediaCharacterLimit = 32 * 1024 * 1024,
  ) {
    if (!Number.isInteger(limit) || limit < 1)
      throw new Error("Некорректный лимит истории.");
    this.media = new HistoryMediaPool(mediaCharacterLimit);
  }

  get current() {
    return this.snapshots[this.cursor];
  }
  get length() {
    return this.cursor + 1;
  }
  get canRedo() {
    return this.cursor < this.snapshots.length - 1;
  }
  get isBatching() {
    return this.batchDepth > 0;
  }
  get stats() {
    return {
      snapshots: this.snapshots.length,
      redo: this.snapshots.length - this.cursor - 1,
      snapshotCharacters: this.snapshots.reduce(
        (sum, entry) => sum + entry.length,
        0,
      ),
      media: this.media.stats,
    };
  }

  capture(state: SceneCanvasState): string {
    return JSON.stringify({
      format: "creator-engine-pro-history",
      version: 1,
      state: {
        ...state,
        assets: state.assets.map((asset) => ({
          ...asset,
          source: this.media.store(asset.source),
        })),
      },
    });
  }

  read(snapshot: string): SceneCanvasState {
    const entry = JSON.parse(snapshot) as {
      format: string;
      version: number;
      state: StoredState;
    };
    if (
      entry.format !== "creator-engine-pro-history" ||
      entry.version !== 1 ||
      entry.state?.schemaVersion !== 1
    )
      throw new Error("Неподдерживаемый снимок истории.");
    return {
      ...entry.state,
      assets: entry.state.assets.map((asset) => ({
        ...asset,
        source: this.media.restore(asset.source),
      })),
    };
  }

  commit(snapshot: string): boolean {
    if (this.current === snapshot) return false;
    this.snapshots.length = this.cursor + 1;
    this.snapshots.push(snapshot);
    if (this.snapshots.length > this.limit) this.snapshots.shift();
    this.cursor = this.snapshots.length - 1;
    return true;
  }

  undo(): string | undefined {
    if (this.length < 2) return undefined;
    this.cursor--;
    return this.current;
  }

  redo(): string | undefined {
    if (!this.canRedo) return undefined;
    this.cursor++;
    return this.current;
  }

  /** Renderer normalization is not a user edit and must not discard Redo. */
  reconcileCurrent(snapshot: string) {
    if (this.cursor >= 0) this.snapshots[this.cursor] = snapshot;
  }

  reset(state: SceneCanvasState) {
    this.media.clear();
    this.batchDepth = 0;
    this.batchPending = false;
    this.snapshots = [this.capture(state)];
    this.cursor = 0;
  }

  beginBatch() {
    this.batchDepth++;
  }
  markBatchPending() {
    this.batchPending = true;
  }
  endBatch(): boolean {
    this.batchDepth = Math.max(0, this.batchDepth - 1);
    if (this.isBatching || !this.batchPending) return false;
    this.batchPending = false;
    return true;
  }
}
